import { useEffect, useMemo, useState } from "react";
import { Select, Tag } from "antd";
import WithAuth from "../../hoc/WithAuth";
import { AuthContextValue, useAuth } from "../../providers/AuthProvider";
import { useGetBanks } from "../../api/banks";
import {
  LoanConfig,
  useGetLoanConfigsForBank,
} from "../../api/loanConfig";
import { DataTable } from "../../components/ui/data-table";
import { Icons } from "../../assets/images/icons";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { AppButton } from "../../components/ui/AppButton";
import { StatusBadge } from "../../components/shared/status-badge";
import LoanConfigModal from "../../components/modals/LoanConfigModal";

/**
 * Bank-scoped loan config CRUD page. The bank picker drives the entire
 * page: every config row, badge and modal binds to the currently selected
 * bank. There's intentionally no "all banks" view because the backend
 * exposes only `/loan-config/bank/{bankId}` listing — keeping the UI
 * symmetric with the API also avoids accidentally cross-mixing rates from
 * different lenders.
 */

const ADMIN_ROLES = new Set(["ROLE_KFT_SUPER_ADMIN", "ROLE_SUPER_ADMIN"]);

function LoanConfigPage() {
  const { user } = useAuth() as AuthContextValue;
  const isAdmin = !!user?.roleName && ADMIN_ROLES.has(user.roleName);

  const [selectedBankId, setSelectedBankId] = useState<number | undefined>();
  const [searchText, setSearchText] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const [open, setOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<LoanConfig | null>(null);

  const { data: banks = [], isLoading: loadingBanks } = useGetBanks();
  const { data: configs = [], isLoading: loadingConfigs } =
    useGetLoanConfigsForBank(selectedBankId);

  // Default-select the first active bank so the page is useful on first load.
  useEffect(() => {
    if (!selectedBankId && banks.length > 0) {
      const firstActive = banks.find((b) => b.isActive) ?? banks[0];
      if (firstActive) setSelectedBankId(firstActive.id);
    }
  }, [banks, selectedBankId]);

  const selectedBank = useMemo(
    () => banks.find((b) => b.id === selectedBankId) ?? null,
    [banks, selectedBankId],
  );

  const filtered = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return configs;
    return configs.filter(
      (c) =>
        (c.loanProductId || "").toLowerCase().includes(term) ||
        (c.qenaApiBaseUrl || "").toLowerCase().includes(term),
    );
  }, [configs, searchText]);

  const paged = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  const columns = [
    {
      key: "loanProductId",
      title: "Loan product id",
      dataIndex: "loanProductId",
      align: "left" as const,
    },
    {
      key: "qenaApiBaseUrl",
      title: "Qena base URL",
      dataIndex: "qenaApiBaseUrl",
      align: "left" as const,
      render: (v: string) => v || "-",
    },
    {
      key: "qenaApiKeyConfigured",
      title: "API key",
      dataIndex: "qenaApiKeyConfigured",
      align: "left" as const,
      render: (v: boolean) =>
        v ? (
          <Tag color="green" className="border-0 bg-green-100 text-green-700">
            Configured
          </Tag>
        ) : (
          <Tag color="red" className="border-0 bg-red-100 text-red-700">
            Missing
          </Tag>
        ),
    },
    {
      key: "loanInterest",
      title: "Interest (%)",
      dataIndex: "loanInterest",
      align: "right" as const,
      render: (v: number) => v?.toLocaleString(undefined, {
        maximumFractionDigits: 2,
      }),
    },
    {
      key: "loanRange",
      title: "Loan range (ETB)",
      dataIndex: "minLoanAmount",
      align: "right" as const,
      render: (_: any, record: LoanConfig) => {
        const min = (record.minLoanAmount ?? 0).toLocaleString();
        const max = (record.maxLoanAmount ?? 0).toLocaleString();
        return `${min} - ${max}`;
      },
    },
    {
      key: "repaymentRange",
      title: "Repayment (days)",
      dataIndex: "minRepaymentDays",
      align: "right" as const,
      render: (_: any, record: LoanConfig) =>
        `${record.minRepaymentDays ?? 0} - ${record.maxRepaymentDays ?? 0}`,
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "status",
      align: "left" as const,
      render: (v: string) => <StatusBadge value={v} />,
    },
  ];

  const actionColumns = [
    {
      key: "actions",
      dataIndex: "id",
      title: "Actions",
      render: (_: any, record: LoanConfig) => (
        <Button
          variant="default"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedConfig(record);
            setOpen(true);
          }}
        >
          <img
            src={Icons.pencilEdit}
            alt="edit"
            className="h-4 w-4 text-gray-100 mr-1"
          />
          Edit
        </Button>
      ),
    },
  ];

  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleCreate = () => {
    setSelectedConfig(null);
    setOpen(true);
  };

  const hideModal = () => {
    setSelectedConfig(null);
    setOpen(false);
  };

  const bankOptions = banks.map((b) => ({
    label: `${b.bankName}${b.code ? ` (${b.code})` : ""}${
      b.isActive ? "" : " — inactive"
    }`,
    value: b.id,
  }));

  return (
    <>
      <div className="hidden md:block rounded-xl bg-white">
        <div className="flex flex-col sm:flex-row gap-3 justify-between mb-3">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Bank
              </label>
              <Select
                value={selectedBankId}
                onChange={(val) => {
                  setSelectedBankId(val);
                  setCurrentPage(1);
                }}
                options={bankOptions}
                style={{ width: 280 }}
                placeholder="Select bank"
                loading={loadingBanks}
                showSearch
                optionFilterProp="label"
              />
            </div>

            <div className="relative">
              <Input
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search by loan product id or URL"
                className="pl-10 rounded-2xl w-[308px] h-11"
              />
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                <img src={Icons.search} alt="search" className="w-6 h-6" />
              </div>
            </div>
          </div>

          <div className="flex gap-2 self-end">
            {isAdmin && (
              <AppButton
                customIcon={Icons.addCircle}
                variant="success"
                onClick={handleCreate}
                disabled={!selectedBankId}
              >
                Create Loan Config
              </AppButton>
            )}
          </div>
        </div>

        {selectedBank && !selectedBank.isActive && (
          <div className="mb-3 p-3 rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-sm">
            <strong>{selectedBank.bankName}</strong> is currently inactive. New
            loan/Qena flows will not pick this bank up; existing configs are
            still visible for audit / reactivation.
          </div>
        )}

        <DataTable<LoanConfig>
          data={paged}
          columns={isAdmin ? [...columns, ...actionColumns] : columns}
          loading={loadingConfigs}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: filtered.length,
            onChange: handlePaginationChange,
          }}
          emptyIcon={<img src={Icons.noDataIcon} alt="No data" />}
          emptyText={
            selectedBankId
              ? "No loan configs found for this bank"
              : "Select a bank to view its loan configs"
          }
          rowKey="id"
        />
      </div>

      <LoanConfigModal
        open={open}
        selectedConfig={selectedConfig}
        banks={banks}
        lockedBankId={selectedBankId}
        hideModal={hideModal}
      />
    </>
  );
}

export default WithAuth(LoanConfigPage);
